<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useGarageStore } from '@/stores/garage.js'
import { useModalStore } from '@/stores/modal.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useDebounceFn } from '@vueuse/core'

// Components
import VehicleCard from '@/components/vehicle/vehicle_card.vue'
import Scrollable from '@/components/scrollable.vue'

// I18n
const { t } = useI18n()

// Stores
const router = useRouter()
const route = useRoute()

const garageStore = useGarageStore()
const modalStore = useModalStore()
const authenticationStore = useAuthenticationStore()

const { currentUser } = storeToRefs(authenticationStore)

// Variables
const isLoading = ref(true)
const isExpandOpen = ref(false)
const expandVehicle = ref(null)
const expandDropdown = ref(null)
const mouseX = ref(0)
const mouseY = ref(0)
const adLoaded = ref(false)

watch(currentUser, (newUser) => {
  if (!newUser) {
    modalStore.setModal(modalStore.modalTypeConstants.LOGIN)
  } else {
    garageStore.getMyGarage()
      .then(() => {
        isLoading.value = false
      })
  }
})

onMounted(async () => {
  if (currentUser.value) {
    await garageStore.getMyGarage()
      .then(() => {
        isLoading.value = false
      })
  } else {
    modalStore.setModal(modalStore.modalTypeConstants.LOGIN)
  }

  document.addEventListener('click', closeExpandOnOutsideClick)
  
  // Check if ad loaded after a short delay
  setTimeout(() => {
    const adElement = document.querySelector('.adsbygoogle')
    if (adElement && adElement.innerHTML.trim() !== '') {
      adLoaded.value = true
    }
  }, 1000)
})

onUnmounted(() => {
  document.removeEventListener('click', closeExpandOnOutsideClick)
})

function closeExpand() {
  isExpandOpen.value = false
  expandVehicle.value = null
}

function closeExpandOnOutsideClick(event) {
  if (expandDropdown.value && !expandDropdown.value.contains(event.target)) {
    closeExpand()
  }
}

function toggleExpand(vehicle, event) {
  event.stopPropagation()
  
  // If clicking on the same vehicle, toggle it
  if (expandVehicle.value && expandVehicle.value.id === vehicle.id) {
    isExpandOpen.value = !isExpandOpen.value
  } else {
    // If clicking on a different vehicle, close current and open new one
    isExpandOpen.value = true
  }
  
  if (isExpandOpen.value) {
    expandDropdown.value = document.querySelector(`.expand-dropdown-${vehicle.id}`)
    expandVehicle.value = vehicle
    mouseX.value = event.pageX
    mouseY.value = event.pageY
  } else {
    expandVehicle.value = null
  }
}

const onFileChange = (event) => {
  garageStore.updateVehicleImage(event.target.files[0]);
}

const addVehicle = useDebounceFn(() => {
  modalStore.setModal(modalStore.modalTypeConstants.ADD_VEHICLE)
}, 250, { maxWait: 1000 })

</script>

<template>
  <div class="grow">
    <div v-if="!isLoading" class="py-4 px-4 lg:px-8 xl:px-16">
      <!-- My Garage Top Banner -->
      <div v-if="adLoaded" class="flex w-full justify-center mb-4 max-h-[90px]">
        <component is="script" async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9802466654600169"
     crossorigin="anonymous"></component>
        <ins
          class="adsbygoogle"
          style="display:inline-block;width:728px;height:90px"
          data-ad-client="ca-pub-9802466654600169"
          data-ad-slot="2293253706"></ins>
        <component is="script">
            (adsbygoogle = window.adsbygoogle || []).push({});
        </component>
      </div>

      <div class="flex flex-row mb-4 flex-wrap gap-2 text-lg breadcrumb-list">
        <p class="font-regular breadcrumb whitespace-nowrap cursor-pointer hover:opacity-70" @click="router.push('/my_garage')">{{ t('my_garage.my_garage') }}</p>
        <p>/</p>
        <p v-if="garageStore.currentVehicle" class="font-regular breadcrumb whitespace-nowrap cursor-pointer hover:opacity-70" @click="router.push('/my_garage/vehicle_profile')">{{ garageStore.currentVehicle.full_vehicle_name }}</p>
        <p>/</p>
        <p class="font-regular breadcrumb active-breadcrumb whitespace-nowrap">{{ t(route.name) }}</p>
      </div>

      <div class="flex flex-row mb-4">
        <p class="text-lg uppercase font-regular select-vehicle">{{ t('my_garage.select_vehicle') }}</p>
        <div v-if="!garageStore.maxVehicles()"
             class="flex items-center justify-center px-2 rounded-full font-regular add-vehicle shadow-md ml-4 cursor-pointer hover:opacity-90"
             @click="addVehicle">
          <span class="px-1 focus:outline-none">
           {{ t('my_garage.add_a_vehicle') }}
          </span>
          <img src="../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1"
               class="h-[22px] w-[22px] pr-1">
        </div>
        <div v-else class="flex items-center justify-center px-2 rounded-full font-regular add-vehicle shadow-md ml-4">
                    <span class="px-1 focus:outline-none">
           {{ t('my_garage.garage_full') }}
          </span>
        </div>
      </div>

      <Scrollable :show-scroll="garageStore.userVehicles.length > 1">
        <VehicleCard v-for="vehicle in garageStore.userVehicles"
                     :key="`vehicle-parent-${vehicle.id}`"
                     :vehicle="vehicle" />
      </Scrollable>

      <hr class="my-6 border-gray-300" />

      <transition enter-active-class="transition ease-out duration-100"
                  leave-active-class="transition ease-in duration-75">
        <div v-if="isExpandOpen && expandVehicle"
             :style="{ top: `${mouseY}px`, left: `calc(${mouseX}px - 12.5rem)` }"
             class="absolute z-10 mt-2 w-48 bg-white rounded-md shadow-lg">
          <ul class="z-10 w-48 bg-white rounded-md shadow-lg right-0 mr-0 overflow-hidden" @click="closeExpand">
            <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
                @click="modalStore.setModal(modalStore.modalTypeConstants.EDIT_VEHICLE, expandVehicle)">
              {{ t('my_garage.edit_vehicle') }}
            </li>
            <label class="block px-4 py-2 cursor-pointer">
              <input type="file" @change="onFileChange" class="hidden">
              <span class="text-sm font-semibold text-gray-700">{{ t('my_garage.vehicle_info_section.vehicle_image_upload') }}</span>
            </label>
            <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
                @click="garageStore.removeVehicle(expandVehicle)">
              {{ t('my_garage.remove_from_garage') }}
            </li>
          </ul>
        </div>
      </transition>

      <div class="flex flex-row pb-4 tab-row">
        <div
          :class="`w-[200px] uppercase text-center px-4 cursor-pointer tab ${route.name === 'my_garage.vehicle_profile' ? 'active-tab' : ''}`"
          @click="router.push('/my_garage/vehicle_profile')">
          <p class="text-lg font-semibold">{{ t('my_garage.vehicle_profile') }}</p>
        </div>
        <div
          :class="`w-[200px] uppercase text-center px-4 cursor-pointer tab ${route.name === 'my_garage.service' || route.name === 'my_garage.quote' ? 'active-tab' : ''}`"
          @click="router.push('/my_garage/service')">
          <p class="text-lg font-semibold">{{ t('my_garage.service') }}</p>
        </div>
      </div>

      <div class="tab-container">
        <router-view />
      </div>

    </div>
    <div v-else class="py-4 px-4 lg:px-8 xl:px-16 animate-pulse">
      <!-- Breadcrumb skeleton -->
      <div class="flex flex-row mb-4 flex-wrap gap-2">
        <div class="h-5 bg-gray-300 rounded w-20"></div>
        <div class="h-5 bg-gray-300 rounded w-4"></div>
        <div class="h-5 bg-gray-300 rounded w-32"></div>
      </div>

      <!-- Select vehicle header skeleton -->
      <div class="flex flex-row mb-4 items-center">
        <div class="h-6 bg-gray-300 rounded w-32"></div>
        <div class="h-8 bg-gray-300 rounded-full w-36 ml-4"></div>
      </div>

      <!-- Vehicle cards skeleton -->
      <div class="flex gap-4 mb-6">
        <div v-for="n in 2" :key="n" class="bg-gray-200 rounded-lg p-4 w-80 h-48">
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 bg-gray-300 rounded"></div>
            <div class="flex-1 space-y-2">
              <div class="h-5 bg-gray-300 rounded w-3/4"></div>
              <div class="h-4 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>

      <hr class="my-6 border-gray-300" />

      <!-- Tabs skeleton -->
      <div class="flex flex-row pb-4 gap-4">
        <div class="h-10 bg-gray-300 rounded w-48"></div>
        <div class="h-10 bg-gray-300 rounded w-48"></div>
      </div>

      <!-- Content skeleton -->
      <div class="space-y-4">
        <div class="h-6 bg-gray-300 rounded w-1/3"></div>
        <div class="h-4 bg-gray-300 rounded w-full"></div>
        <div class="h-4 bg-gray-300 rounded w-2/3"></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/my_garage/my_garage';
</style>
